import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, downloadBlobGet, fetchTextGet, getRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import UtilContext from '../../components/UtilContext';
import { AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon, RefreshIcon } from '../svg/svgIcons';
import { DateTime } from 'luxon';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import { PanelMenu } from 'primereact/panelmenu';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../utils/tabulatorScroll';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse } from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { dateUserHeaderFilter } from '../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import countries from "i18n-iso-countries";
import "i18n-iso-countries/langs/sk";
import "i18n-iso-countries/langs/en";
import "i18n-iso-countries/langs/de";
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

import {
  formatBusinessPartnerTypeText,
  getBusinessPartnerTypeSet,
  parseCompanyPartnerHeaderMulti,
} from '../../utils/businessPartnerTypeDisplay';
countries.registerLocale(require("i18n-iso-countries/langs/sk"));
countries.registerLocale(require("i18n-iso-countries/langs/en"));
countries.registerLocale(require("i18n-iso-countries/langs/de"));


const DEFAULT_COLUMNS_VISIBLE_COMPANY = Object.freeze({
  id: true,
  name: true,
  types: true,
  defined_id: true,
  country: true,
  address: true,
  tel_number: true,
  email: true,
  web_address: true,
  contact_person: true,
  description: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_COMPANY };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_COMPANY };
  return merged;
}

function normalizeCompanyColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY)) out[field] = stateObj?.[field] !== false;
  return out;
}

function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY)) out[field] = raw?.[field] !== false;
  return out;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try { table.setHeaderFilterValue?.(field, '', true); } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function countVisibleDataColumns(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY).filter((k) => vis[k] !== false).length;
}

/** Polia masky = kľúče v `DEFAULT_COLUMNS_VISIBLE_COMPANY`; hodnoty = i18n kľúče stĺpcov. */
const COMPANY_MASK_FIELD_I18N = Object.freeze({
  id: 'BusinessPartnerList.Column.Id',
  name: 'BusinessPartnerList.Column.Name',
  types: 'BusinessPartnerList.Column.PartnerType',
  defined_id: 'BusinessPartnerList.Column.DefinedId',
  country: 'BusinessPartnerList.Column.Country',
  address: 'BusinessPartnerList.Column.Address',
  tel_number: 'BusinessPartnerList.Column.TelNumber',
  email: 'BusinessPartnerList.Column.Email',
  web_address: 'BusinessPartnerList.Column.WebAddress',
  contact_person: 'BusinessPartnerList.Column.ContactPerson',
  description: 'BusinessPartnerList.Column.Description',
  created_at: 'BusinessPartnerList.Column.CreatedAt',
  updated_at: 'BusinessPartnerList.Column.UpdatedAt',
});

const BUSINESS_PARTNER_COLUMN_I18N_KEYS = COMPANY_MASK_FIELD_I18N;

const normalizeText = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function BusinessPartnerList() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const { setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter, openChosenNotificationPreferences, getDateFormat, formatDate, getUserInitials, getUserColor } = useContext(UtilContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_COMPANY });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const countryNamesLocale = locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb' ? 'en' : 'sk');
  const companyPartnerTypeFilterOptions = useMemo(() => ([
    { label: t('BusinessPartnerList.PartnerType.Customer'), value: 'customer' },
    { label: t('BusinessPartnerList.PartnerType.Client'), value: 'client' },
    { label: t('BusinessPartnerList.PartnerType.Supplier'), value: 'supplier' },
    { label: t('BusinessPartnerList.PartnerType.CalibrationCompany'), value: 'calibration_company' },
    { label: t('BusinessPartnerList.PartnerType.TrainingCompany'), value: 'training_company' },
    { label: t('BusinessPartnerList.PartnerType.ServiceCompany'), value: 'service_company' },
  ]), [t]);
  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === 'BusinessPartnerList');
    if (!row) return false;
    const attributes = safeParse(row.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters]);

  const reloadCompanies = useCallback(() => {
    getRequest('/api/companies').then((response) => dispatch({ type: 'load_companies', newCompanies: response.data }));
  }, [dispatch]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit settings');
    const canAdd = user?.permissions?.includes('add settings');
    const canDelete = user?.permissions?.includes('delete settings');
    setEditIcon(!canEdit || count !== 1);
    setCopyIcon(!canAdd || count !== 1);
    setDeleteIcon(!canDelete || count < 1);
  }, [user?.permissions]);

  const deleteSelected = useCallback(() => {
    const rows = getTable()?.getSelectedData?.() ?? [];
    Promise.all(rows.map((row) => deleteRequest(`/api/companies/${row.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('BusinessPartnerList.Toast.DeletedSummary'), life: 3000, detail: t('BusinessPartnerList.Toast.DeletedDetail') });
        reloadCompanies();
      })
      .catch((e) => toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.RejectedSummary'), detail: e?.message || t('BusinessPartnerList.Toast.DeleteFailedDetail'), life: 3000 }));
  }, [getTable, reloadCompanies, t, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
    });
  }, [deleteSelected, t]);

  function onEditRow(business_partner) {
    dispatch({ type: 'create_new_tab', name: t('BusinessPartnerList.Tab.Edit'), translationKey: 'BusinessPartnerList.Tab.Edit', legacyLabel: t('BusinessPartnerList.Tab.Edit'), page: 'BusinessPartnerAdd', props: { business_partner, type: 'edit', not_last_used: true }, mask: 'gset008' });
  }
  
  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return <div className="flex items-center justify-center w-full h-full"><button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button></div>;
  }

  function CountryFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const text = rowData?.country ? countries.getName(rowData.country, countryNamesLocale) || rowData.country : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function TypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const text = formatBusinessPartnerTypeText(rowData, t);
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={text || undefined}>{text}</span>;
  }

  function TruncatePlainTextFormatter(props) {
    const v = props.cell.getValue();
    const s = v != null ? String(v) : '';
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={s || undefined}>{s}</span>;
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.contractor;
    const raw = rowData?.created_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }} /> : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const u = rowData?.last_touched_by_user;
    const raw = rowData?.updated_at;
    const text = formatDate ? formatDate(raw, dateTimeFmt, true) : DateTime.fromISO(String(raw || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {u ? <Avatar className="shrink-0" title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff' }} /> : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  const textHeaderFilter = useCallback((headerValue, rowValue) => {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }, []);

  const dateOrUserFilter = useCallback((headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = normalizeText(raw);
    const userObj = filterParams?.name === 'created_at' ? rowData?.contractor : rowData?.last_touched_by_user;
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = normalizeText(`${uname} ${dateStr}`);
    return hay.includes(search);
  }, [dateFmt, dateTimeFmt]);

  const columns = useMemo(() => {
    const colTitle = (key) => t(key);
    return [
      { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false },
      { title: '', field: 'editor', hozAlign: 'center', width: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false },
      { title: colTitle('BusinessPartnerList.Column.Id'), field: 'id', minWidth: TABULATOR_LIST_ID_MIN_WIDTH, widthGrow: 0.6, hozAlign: 'left', sorter: 'number', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter },
      { title: colTitle('BusinessPartnerList.Column.Name'), field: 'name', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.Name')), widthGrow: 1.4 },
      {
        title: colTitle('BusinessPartnerList.Column.PartnerType'),
        field: 'types',
        hozAlign: 'left',
        formatter: reactFormatter(<TypeFormatter />),
        minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.PartnerType'), 'multiselect'),
        widthGrow: 1.2,
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: companyPartnerTypeFilterOptions },
        headerFilterFunc: (headerValue, rowValue, rowData) => {
          const selected = parseCompanyPartnerHeaderMulti(headerValue);
          if (selected.length === 0) return true;
          const rowTypes = getBusinessPartnerTypeSet(rowData);
          return selected.some((s) => rowTypes.has(String(s)));
        },
      },
      { title: colTitle('BusinessPartnerList.Column.DefinedId'), field: 'defined_id', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.DefinedId')), widthGrow: 0.9 },
      { title: colTitle('BusinessPartnerList.Column.Country'), field: 'country', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.Country')), widthGrow: 1, formatter: reactFormatter(<CountryFormatter />), headerFilterFunc: (headerValue, rowValue, rowData) => normalizeText(countries.getName(rowData?.country || '', countryNamesLocale) || rowData?.country || '').includes(normalizeText(headerValue)) },
      { title: colTitle('BusinessPartnerList.Column.Address'), field: 'address', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.Address')), widthGrow: 1.2 },
      { title: colTitle('BusinessPartnerList.Column.TelNumber'), field: 'tel_number', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.TelNumber')), widthGrow: 0.8 },
      { title: colTitle('BusinessPartnerList.Column.Email'), field: 'email', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.Email')), widthGrow: 1.2 },
      { title: colTitle('BusinessPartnerList.Column.WebAddress'), field: 'web_address', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.WebAddress')), widthGrow: 1 },
      { title: colTitle('BusinessPartnerList.Column.ContactPerson'), field: 'contact_person', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.ContactPerson')), widthGrow: 1 },
      { title: colTitle('BusinessPartnerList.Column.Description'), field: 'description', hozAlign: 'left', formatter: reactFormatter(<TruncatePlainTextFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: textHeaderFilter, minWidth: tabulatorColumnMinWidthFromTitle(colTitle('BusinessPartnerList.Column.Description')), widthGrow: 1.6 },
      { title: colTitle('BusinessPartnerList.Column.CreatedAt'), field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('BusinessPartnerList.Column.CreatedAt')), widthGrow: 1, formatter: reactFormatter(<CreatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
      { title: colTitle('BusinessPartnerList.Column.UpdatedAt'), field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(colTitle('BusinessPartnerList.Column.UpdatedAt')), widthGrow: 1, formatter: reactFormatter(<UpdatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
    ];
  }, [companyPartnerTypeFilterOptions, countryNamesLocale, dateFmt, dateOrUserFilter, dateTimeFmt, t, textHeaderFilter]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: BUSINESS_PARTNER_COLUMN_I18N_KEYS,
      headerFilters: {
        types: { placeholder: t('Common.All'), options: companyPartnerTypeFilterOptions },
      },
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable, companyPartnerTypeFilterOptions]);


  const getMaskColumnLabel = useCallback((field) => {
    const key = COMPANY_MASK_FIELD_I18N[field];
    return key ? t(key) : field;
  }, [t]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false || seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [columnsVisible, getTable]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const buildCompanyExportQueryString = useCallback((opts = {}) => {
    const params = {
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(getTable()?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams]);

  const runCompanyPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildCompanyExportQueryString({ includePrintColumns: true, printLayout: printLayoutChoice, printOrientation: printOrientationChoice, printColumns: getPrintColumnsOrder() });
      const html = await fetchTextGet(`/api/companies/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('BusinessPartnerList.Toast.PrintTitle'), detail: e?.message || t('BusinessPartnerList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildCompanyExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportCompanyXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildCompanyExportQueryString({ includePrintColumns: true, printColumns: getPrintColumnsOrder() });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.BUSINESS_PARTNERS));
      const { blob, filename } = await downloadBlobGet(`/api/companies/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('BusinessPartnerList.Toast.ExportTitle'), detail: e?.message || t('BusinessPartnerList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildCompanyExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeCompanyColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.MaskWarnTitle'), detail: t('BusinessPartnerList.Toast.SelectOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter('BusinessPartnerList', 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message: t('BusinessPartnerList.SaveStandardMaskMessage'),
      header: t('BusinessPartnerList.SaveStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) return;
        setStandardMaskFilter('BusinessPartnerList', columnsFromTabulatorForStandardMaskSave(layout.columns), layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === 'BusinessPartnerList');
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.StandardMaskWarnTitle'), detail: t('BusinessPartnerList.StandardMaskMissing'), life: 4000 });
      return;
    }
    const attributes = safeParse(standard.attributes, []);
    if (!Array.isArray(attributes) || attributes.length <= 1) return;
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeCompanyColumnVisibilityForSave(mergedCols);
    confirmDialog({
      message: t('BusinessPartnerList.ApplyStandardMaskMessage'),
      header: t('BusinessPartnerList.ApplyStandardMaskHeader'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter('BusinessPartnerList', 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try { table.setColumnLayout?.(attributes); } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => { isRestoringRef.current = false; }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_COMPANY, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_COMPANY, ...columnsVisible, [field]: value };
    if (value === false && countVisibleDataColumns(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('BusinessPartnerList.Toast.MaskWarnTitle'), detail: t('BusinessPartnerList.Toast.AtLeastOneColumn'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_COMPANY };
    Object.keys(next).forEach((k) => { next[k] = true; });
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY).forEach((k) => { next[k] = false; });
    next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const openMaskDialog = () => {
    setColumnsVisible(mergeVisibility(columnsVisible));
    setMaskVisible(true);
    closeMenu();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) setExpandedKeys({});
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = useMemo(() => [{
    key: '0',
    label: t('Common.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter('BusinessPartnerList', 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('Common.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter('BusinessPartnerList', 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('Common.SetSorting'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter('BusinessPartnerList', 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('Common.ClearSorting'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter('BusinessPartnerList', 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('Common.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask ? [{ key: '0_5s', label: t('Common.StandardMask'), icon: 'pi pi-clone', command: confirmApplyStandardLayoutToUserMask, template: itemRenderer }] : []),
      { key: '0_6', label: t('ServiceTask.Notifications'), icon: 'pi pi-envelope', command: () => { openChosenNotificationPreferences('BusinessPartnerList'); closeMenu(); }, template: itemRenderer },
    ],
  }], [closeMenu, confirmApplyStandardLayoutToUserMask, getTable, hasApplicableStandardMask, openChosenNotificationPreferences, openMaskDialog, setUserMaskFilter, t]);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') table.clearFilter(true);
    else {
      const needle = normalizeText(normalized);
      table.setFilter((data) => normalizeText(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={!user?.permissions?.includes('add settings')} onClick={() => dispatch({ type: 'create_new_tab', name: t('BusinessPartnerList.Tab.New'), translationKey: 'BusinessPartnerList.Tab.New', legacyLabel: t('BusinessPartnerList.Tab.New'), page: 'BusinessPartnerAdd', props: { business_partner: null, type: 'add' }, mask: 'gset008' })}><AddIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('BusinessPartnerList.Tab.Edit'), translationKey: 'BusinessPartnerList.Tab.Edit', legacyLabel: t('BusinessPartnerList.Tab.Edit'), page: 'BusinessPartnerAdd', props: { business_partner: getTable()?.getSelectedData?.()?.[0], type: 'edit', not_last_used: true }, mask: 'gset008' })}><EditIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('BusinessPartnerList.Tab.New'), translationKey: 'BusinessPartnerList.Tab.New', legacyLabel: t('BusinessPartnerList.Tab.New'), page: 'BusinessPartnerAdd', props: { business_partner: getTable()?.getSelectedData?.()?.[0], type: 'copy', not_last_used: true }, mask: 'gset008' })}><CopyIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon}><DeleteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : () => { setSearchIconColor(''); setSearchValue(''); submittedSearchValueRef.current = ''; getTable()?.clearFilter?.(true); }}>{searchIconColor === '' ? <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" /> : <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />}</button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={exportCompanyXlsx} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={reloadCompanies}><RefreshIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollToFirst} title={t('Icon.First')}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollUp} title={t('Icon.Previous')}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollDown} title={t('Icon.Next')}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={scrollToLast} title={t('Icon.Last')}><ForwardToLastIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog(t('BusinessPartnerList.FavoriteTitle'), 'BusinessPartnerList')}><FavoriteIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView('BusinessPartnerList', columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key="business-partner-list"
                data={state.companies}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask('BusinessPartnerList', state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      isRestoringRef.current = true;
                      const attributes = safeParse(effective.standardMask.attributes, []);
                      const jsonColumns = safeParse(effective.standardMask.columns, {});
                      if (Array.isArray(attributes) && attributes.length > 1) table.setColumnLayout?.(attributes);
                      if (jsonColumns && typeof jsonColumns === 'object') {
                        const merged = mergeVisibility(jsonColumns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else {
                      applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE_COMPANY);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents('BusinessPartnerList', ref, setUserMaskFilter),
                }}
                options={{ renderHorizontal: 'virtual', layout: 'fitColumns', movableColumns: true, autoResize: true, headerSortTristate: true, filterMode: 'local' }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog visible={printChoiceVisible} onHide={() => setPrintChoiceVisible(false)} orientation={printOrientationChoice} onOrientationChange={setPrintOrientationChoice} layout={printLayoutChoice} onLayoutChange={setPrintLayoutChoice} onConfirm={runCompanyPrint} />

      <Dialog header={t('BusinessPartnerList.SearchDialogTitle')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('BusinessPartnerList.SearchHint')}</p>
          <div className="flex items-center gap-3 w-full">
            <input value={searchValue} onChange={(e) => setSearchValue(e.target.value)} type="text" className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400" />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center"><SearchIcon /></button>
          </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('BusinessPartnerList.MaskDialogTitle')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_COMPANY)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('BusinessPartnerList.MaskSelectAll')}
        deselectAllLabel={t('BusinessPartnerList.MaskDeselectAll')}
        saveLabel={t('BusinessPartnerList.MaskSave')}
        getFieldLabel={getMaskColumnLabel}
        inputIdPrefix="company-mask"
      />
    </>
  );
}